import React from 'react';
import { useQuery, useMutation } from '@apollo/client';
import * as graphql from '@ucc/graphql/documents/roles';
import { useNavigate } from 'react-router-dom';
import { tables } from '~/pages/auth/meta/data/auth.schema';
import { withRenderers } from '~/utils/withRenderers';
import { permissionGroups as permissionGroupsConfig } from '../config/permissionGroups';
import { useDeleteWithConfirmation } from '~/hooks/useDeleteWithConfirmation';
import { usePermission } from '~/hooks';
import { ListBranchesDocument, SortOrder } from '~/gql/ucpw/graphql';
import {
    mergeBranchNiederlassungenIntoPermissionGroups,
    type NiederlassungRow,
} from '../utils/mergeBranchNiederlassungenIntoPermissionGroups';
import { mergePermissionGroupsFromRemote } from '../utils/mergePermissionGroupsFromRemote';

export const useAuth = ({ id }: { id?: string } = {}) => {
    const navigate = useNavigate();
    const { canEdit, canDelete } = usePermission('staff.permissions');

    // ===============================================
    // LIST
    // ===============================================

    const {
        data: listData,
        loading: listLoading,
        error: listError,
    } = useQuery(graphql.listRoles, { skip: !!id });
    // const roles = listData?.security?.roles?.data || []; // all roles including Admin
    const roles = listData?.security?.roles?.data?.filter((role: any) => !role.system) || [];

    // ===============================================
    // GET
    // ===============================================

    const isNew = !id || id === 'neu';

    const {
        data: getData,
        loading: getLoading,
        error: getError,
    } = useQuery(graphql.getRole, {
        variables: { id: id! },
        skip: isNew,
    });
    const role = getData?.security?.role?.data;
    /** Kein `|| []` – sonst neue Array-Referenz jedes Renders → useMemo/RoleForm-reset-Loop */
    const rolePermissionGroups = role?.permissionGroups;

    const { data: branchesData, loading: branchesLoading } = useQuery(ListBranchesDocument, {
        variables: {
            limit: 1000,
            filter: { bezeichnungExists: true },
            orderBy: [{ nummer: SortOrder.Asc }],
        },
        context: { clientName: 'ucpw' },
    });
    const branchRows = branchesData?.items?.items as NiederlassungRow[] | undefined;

    const permissionGroupsConfigWithBranches = React.useMemo(
        () => mergeBranchNiederlassungenIntoPermissionGroups(permissionGroupsConfig, branchRows),
        [branchRows]
    );

    const permissionGroups = React.useMemo(
        () =>
            rolePermissionGroups?.length
                ? mergePermissionGroupsFromRemote(
                      permissionGroupsConfigWithBranches,
                      rolePermissionGroups
                  )
                : permissionGroupsConfigWithBranches,
        [permissionGroupsConfigWithBranches, rolePermissionGroups]
    );

    const refetchQueries = [
        { query: graphql.listRoles },
        { query: graphql.getRole, variables: { id } },
    ];

    // ===============================================
    // CREATE
    // ===============================================

    const [createMutation] = useMutation(graphql.createRole, {
        refetchQueries,
    });

    // ===============================================
    // UPDATE
    // ===============================================

    const [updateMutation] = useMutation(graphql.updateRole, {
        refetchQueries,
    });

    // ===============================================
    // DELETE
    // ===============================================

    const [deleteMutation] = useMutation(graphql.deleteRole, {
        refetchQueries,
    });

    const { deleteWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation,
        refetchQueries,
    });

    // ===============================================
    // TABLE
    // ===============================================

    const controls = [
        {
            title: canEdit ? 'Bearbeiten' : 'Ansehen',
            props: {
                onClick: (row: any) => {
                    navigate(`/personal/berechtigungen/${row?.original?.id}`);
                },
            },
        },
        canEdit && {
            title: 'Kopieren',
            props: {
                onClick: (row: any) => {
                    navigate(`/personal/berechtigungen/${row?.original?.id}`, {
                        state: {
                            isCopy: true,
                        },
                    });
                },
            },
        },
        canEdit && canDelete && 'divider',
        canDelete && {
            title: 'Löschen',
            props: {
                color: 'red.400',
                onClick: (row: any) => {
                    deleteWithConfirmation({ id: row?.original?.id });
                },
            },
        },
    ].filter(Boolean);

    const columns = React.useMemo(() => withRenderers(tables.roles.columns, controls), []);
    const hiddenColumns = tables.roles.hiddenColumns;
    const pageSize = tables?.roles?.pageSize || 50;

    const isReadLoading = listLoading || getLoading || branchesLoading;
    const isReadError = listError || getError;

    return {
        roles,
        role: { ...role, permissionGroups },
        isReadLoading,
        isReadError,
        columns,
        hiddenColumns,
        pageSize,
        createMutation,
        updateMutation,
        deleteMutation,
        deleteWithConfirmation,
    };
};
